import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  cn,
  ControlCode,
  ControlCodes,
  Detection,
  getControlName,
  queries,
  SigmaNonArchPlatform,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  CheckmarkIcon,
  CloseIcon,
  ControlIcon,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  RotatingChevron,
  PlatformIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { usePartnerBlockMutationWithToast } from "@/hooks/partner-mutations";
import { usePopper } from "@/hooks/use-popper";
import { DefaultTooltip } from "../default-tooltip";

function useDetections(testId: string) {
  const authConfig = useAuthConfig();

  return useSuspenseQuery({
    ...queries.detect.detections(authConfig),
    select: (detections) =>
      detections.filter((detection) => detection.test === testId),
  });
}

function useReports(testId: string, controlCode: ControlCode) {
  const authConfig = useAuthConfig();

  return useQuery({
    ...queries.partner.reports(authConfig, controlCode),
    select: (reports) => reports.filter((report) => report.test_id === testId),
  });
}

const platforms: SigmaNonArchPlatform[] = [
  "windows",
  "linux",
  "macos",
] as const;

function getNonSigmaDOSName(platform: SigmaNonArchPlatform) {
  if (platform === "macos") {
    return "darwin";
  }
  return platform;
}

export function MDPAction({ id }: { id: string }) {
  const authConfig = useAuthConfig();
  const detections = useDetections(id);

  const { data: controlCodes } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) => account.controls.map((c) => c.id),
  });

  if (detections.data.length === 0) {
    return null;
  }

  const controlCode = controlCodes[0];

  if (controlCodes.length === 1 && controlCode) {
    return (
      <SingleControlMDPAction
        id={id}
        detections={detections.data}
        controlCode={controlCode}
      />
    );
  }

  return (
    <MultiControlMDPAction
      id={id}
      detections={detections.data}
      controlCodes={controlCodes}
    />
  );
}

const supportedControlCodes: Set<ControlCode> = new Set([
  ControlCodes.CROWDSTRIKE,
  ControlCodes.DEFENDER,
]);

function MultiControlMDPAction({
  id,
  detections,
  controlCodes,
}: {
  id: string;
  detections: Detection[];
  controlCodes: ControlCode[];
}) {
  const dropdown = usePopper();
  const [selectedControlCode, setSelectedControlCode] = useState<
    ControlCode | undefined
  >(
    controlCodes.includes(ControlCodes.CROWDSTRIKE)
      ? ControlCodes.CROWDSTRIKE
      : controlCodes[0],
  );

  if (!selectedControlCode) {
    return null;
  }

  const accountControlCodes = controlCodes.filter((controlCode) =>
    supportedControlCodes.has(controlCode),
  );

  return (
    <div className="flex flex-row gap-3">
      <Dropdown {...dropdown}>
        <DropdownTrigger asChild>
          <Button
            size="tiny"
            intent="pill"
            extraIcon={
              <RotatingChevron active={dropdown.open} className={"ml-auto"} />
            }
          >
            <ControlIcon control={selectedControlCode} />
          </Button>
        </DropdownTrigger>
        <DropdownContent align="start">
          {accountControlCodes.map((controlCode) => (
            <DropdownItem
              key={controlCode}
              onSelect={() => setSelectedControlCode(controlCode)}
            >
              <div className="flex flex-row items-center gap-2">
                <ControlIcon control={controlCode} />
                {getControlName(controlCode)}
              </div>
            </DropdownItem>
          ))}
        </DropdownContent>
      </Dropdown>

      <SendDetectionAction id={id} controlCode={selectedControlCode} hideIcon />
      <ReportButtons
        testId={id}
        detections={detections}
        controlCode={selectedControlCode}
      />
    </div>
  );
}

function SingleControlMDPAction({
  id,
  detections,
  controlCode,
}: {
  id: string;
  detections: Detection[];
  controlCode: ControlCode;
}) {
  return (
    <div className="flex flex-row gap-3">
      <SendDetectionAction id={id} controlCode={controlCode} />
      <ReportButtons
        testId={id}
        detections={detections}
        controlCode={controlCode}
      />
    </div>
  );
}

function ReportButtons({
  testId,
  detections,
  controlCode,
}: {
  testId: string;
  detections: Detection[];
  controlCode: ControlCode;
}) {
  const reportsQuery = useReports(testId, controlCode);
  const blockMutation = usePartnerBlockMutationWithToast();

  if (reportsQuery.isLoading || !reportsQuery.data) {
    return null;
  }

  const reports = reportsQuery.data;

  if (reports.length === 0) {
    return null;
  }

  const detectionPlatforms = new Set(
    detections.map(
      (detection) =>
        (detection.rule.logsource as { product: SigmaNonArchPlatform }).product,
    ),
  );

  const reportPlatforms = new Set(reports.map((report) => report.platform));

  const supportedPlatforms = platforms.filter(
    (platform) =>
      reportPlatforms.has(platform) || detectionPlatforms.has(platform),
  );

  return (
    <div className="flex justify-end gap-3">
      {supportedPlatforms
        .sort((a, b) =>
          getNonSigmaDOSName(a).localeCompare(getNonSigmaDOSName(b)),
        )
        .map((platform) => {
          const report = reports.find((r) => r.platform === platform);
          const status = report && "created";

          let Icon = CloseIcon;

          if (status === "created") {
            Icon = CheckmarkIcon;
          }

          return (
            <DefaultTooltip
              disabled
              asChild
              key={platform}
              content={
                <div className="flex flex-col items-center gap-2">
                  <div className="flex flex-row gap-3">
                    <div>
                      <span className="text-secondary-foreground">
                        Monitored
                      </span>{" "}
                      {report?.monitored}
                    </div>
                    <div>
                      <span className="text-secondary-foreground">
                        Detected
                      </span>{" "}
                      {report?.detected}
                    </div>
                    <div>
                      <span className="text-secondary-foreground">Blocked</span>{" "}
                      {report?.blocked}
                    </div>
                  </div>

                  <div className="flex w-full flex-row gap-3">
                    <Button
                      loading={blockMutation.isPending}
                      onClick={() =>
                        blockMutation.mutate({
                          controlCode,
                          testId,
                        })
                      }
                      intent="secondary"
                    >
                      Re-send IOA
                    </Button>

                    {/* TODO <Button intent="secondary" extraIcon={<ExternalLinkIcon />}>
                    View IOA
                  </Button> */}
                  </div>
                </div>
              }
            >
              <div
                className={cn(
                  "flex items-center gap-1 border-stroke-line",
                  status === "created" && "border-stroke-input",
                )}
              >
                <Icon
                  className={cn(
                    "text-secondary-foreground",
                    status === "created" && "text-status-green",
                  )}
                />
                <PlatformIcon platform={platform} />
              </div>
            </DefaultTooltip>
          );
        })}
    </div>
  );
}

export function SendDetectionAction({
  id,
  controlCode,
  hideIcon = false,
}: {
  id: string;
  controlCode: ControlCode;
  hideIcon?: boolean;
}) {
  const blockMutation = usePartnerBlockMutationWithToast();

  return (
    <Button
      size="tiny"
      loading={blockMutation.isPending}
      onClick={() =>
        blockMutation.mutate({
          controlCode,
          testId: id,
        })
      }
      intent="primary-pill"
      icon={hideIcon ? undefined : <ControlIcon control={controlCode} />}
    >
      Send
    </Button>
  );
}
